import { TOKEN_CONFIG } from '@/constants/tokens';
import { appConfigAtom, appDataAtom, appEventsAtom } from '@/state/app';
import { walletAtom, walletIsValidNetwork } from '@/state/wallet';
import { getNetworkByChainId, getNetworkByToken } from '@/utils/utils';
import { BigNumber } from 'ethers';
import { useCallback, useEffect, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useContracts } from '@/hooks/staking/contracts';
import { WithdrawOrder } from '@/constants/types';

const defaultValues = {
    withdrawOrders: [] as WithdrawOrder[],
};

export const getAllUserOrders = async (
    ordersFunction: (page: number) => Promise<Array<any>>,
    formatFunction: (response: Array<any>, serverTime: BigNumber) => Array<any>,
) => {
    const results = await ordersFunction(0);
    if (!results) return [];
    const maxPages = results[2].toNumber();
    if (maxPages === 0) {
        return formatFunction(results[0], results[1]);
    } else {
        const allOrdersQuery = Array.from({ length: maxPages - 1 }, (_, i) => {
            return ordersFunction(i + 1);
        });
        const allOrders = await Promise.all(allOrdersQuery);
        const orders = allOrders.flatMap((result) => result[0]);
        return formatFunction([...results[0], ...orders], results[1]);
    }
};

export const useWithdrawOrders = () => {
    const setData = useSetRecoilState(appDataAtom);
    const walletState = useRecoilValue(walletAtom);
    const appConfig = useRecoilValue(appConfigAtom);
    const appEvents = useRecoilValue(appEventsAtom);
    const isWalletValidNetwork = useRecoilValue(walletIsValidNetwork);
    const network = getNetworkByChainId(walletState.chainId!);
    const [state, setState] = useState(defaultValues);
    const [loaded, setLoaded] = useState(false);
    const { getEpoch, getUserOrders, getWithdrawLogs, getClaimLogs } = useContracts();
    const isPolygonNetwork = network?._id === 'mumbai' || network?._id === 'polygon';
    const isEthereum = appConfig.token === 'CSETH';

    const getUserOrdersFormatted = useCallback(
        (response: Array<any>, serverTime: BigNumber) => {
            const orders = response!
                .filter((order) => order.orderId !== 0)
                .map((order) => ({
                    amount: order.amount,
                    claimTime: order.claimableAt.mul(1000).toNumber(),
                    currentTime: serverTime.mul(1000).toNumber(),
                    epochsLeft: 0,
                    fee: order.fee,
                    isClaimable: order.isClaimable,
                    isEarlyClaimable: order.isEarlyClaimable,
                    orderId: order.orderId.toNumber(),
                    penalty: BigNumber.from(0),
                    txHash: null,
                    isClaimed: order.amount.eq(0),
                    user: walletState.selectedAddress,
                })) as WithdrawOrder[];

            return orders;
        },
        [walletState.selectedAddress],
    );

    // Initial Load
    useEffect(() => {
        getWithdrawOrders(true).then();
    }, [walletState.selectedAddress, walletState.chainId, appConfig.token]);

    // Refresh on Unstake & Claim events
    useEffect(() => {
        if (loaded) getWithdrawOrders(false).then();
    }, [appEvents.unstakeEvent, appEvents.claimEvent, appEvents.updateValuesEvent]);

    const getWithdrawOrders = async (reset: boolean) => {
        let toClaimOrders = defaultValues.withdrawOrders;

        if (isWalletValidNetwork && walletState.selectedAddress && appConfig.token) {
            // Reset first if different
            if (reset) setState(defaultValues);

            // Using Contract to get
            if (isPolygonNetwork || isEthereum) {
                toClaimOrders = await getAllUserOrders(getUserOrders, getUserOrdersFormatted);
                toClaimOrders = toClaimOrders.filter((x) => !x.isClaimed);
                setState({
                    withdrawOrders: toClaimOrders,
                });
            } else {
                // get withdraw and claims events
                const [claims, withdraws, epochInfo] = await Promise.all([
                    getClaimLogs(),
                    getWithdrawLogs(),
                    getEpoch(),
                ]);

                // filter out claimed ones
                const claimedIds = claims.map((x) => x.orderId.toString());
                const toClaim = withdraws.filter((x) => !claimedIds.includes(x.orderId.toString()));

                const withdrawOrdersQuery: Promise<WithdrawOrder>[] = toClaim.map(async (log) => {
                    const { user, orderId, amount, fee, epoch } = log;

                    // calculate when is it claimable = number of epochs left * avg epoch time
                    let claimTime;
                    let isClaimable;
                    let currentTime;
                    const currentEpoch = epochInfo[0] as BigNumber;
                    const withdrawCycle = epochInfo[1] as BigNumber;
                    const withdrawStep = epoch.add(withdrawCycle);
                    let checkpointsLeft = 0;

                    // MATIC cases - Calculates withdraw time based on avg epoch time
                    isClaimable = (<BigNumber>currentEpoch)!.gt(withdrawStep);

                    checkpointsLeft = withdrawStep.sub(currentEpoch).toNumber() + 1;
                    const network = getNetworkByToken(appConfig.token)._id;
                    const extraTime = checkpointsLeft * TOKEN_CONFIG[appConfig.token]!.checkpointTime[network] * 1000;
                    const now = new Date();
                    currentTime = now.getTime();
                    claimTime = currentTime + extraTime;

                    return {
                        orderId: BigNumber.from(orderId).toNumber(),
                        amount: BigNumber.from(amount),
                        fee: BigNumber.from(fee),
                        isClaimable: isClaimable,
                        isClaimed: false,
                        user,
                        claimTime,
                        currentTime,
                        epochsLeft: checkpointsLeft,
                    };
                });
                toClaimOrders = await Promise.all(withdrawOrdersQuery);
                setState({
                    withdrawOrders: toClaimOrders,
                });
            }
        } else {
            setState(defaultValues);
        }

        // Update Claimable Balance
        const claimableAmount = toClaimOrders.reduce((acc, order) => {
            acc = acc.add(order.amount);
            if (order.fee) acc = acc.sub(order.fee);
            return acc;
        }, BigNumber.from(0));
        setData((previous) => ({ ...previous, claimableAmount, isClaimsLoading: false }));
        setLoaded(true);
    };

    return {
        withdrawOrders: state.withdrawOrders,
    };
};
